import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import {
  Modal,
  Col,
  Card,
  Button,
  ButtonGroup,
  Alert,
  Form,
  Tabs,
  Tab,
  Spinner,
} from 'react-bootstrap';
import SelectComponent from '../Select';
import useConvenios from '../../../hooks/useConvenios';
import useProcedimentos from '../../../hooks/useProcedimentos';
import useGetNomeMedico from '../../../hooks/useGetNomeMedico';
import NovoMedicoForm from '../Forms/NovoMedicoForm';
import { dbatividade } from '../../../helpers/agenda/convenios';
import { goToNextInput } from '../../../util/utils';

const memoAtividade = dbatividade.atividade.map(i => i.codProcedi);

const FaturamentoModalCreate = ({
  show,
  onHide,
  loading = false,
  fatura,
  setFatura,
  selectMedicos,
  medicos,
  handleChangeFaturamento,
  handleValorFaturamento,
  setQuantidadeFetos,
  optarPorEcg,
  handleInsertFaturamento,
  handleEspelhoTrans,
  handleEspelhoFarmaco,
  handleEspelhoFisio,
  handleEspelhoDoppler,
  handleImprimirEspelho,
}) => {
  const { convenios, opcoesConvenio } = useConvenios();
  const { opcoesProcedimento } = useProcedimentos();
  const [successMsg, setSuccessMsg] = useState('');
  const [showAlertEspelho, setShowAlertEspelho] = useState(false);
  const [faturado, setFaturado] = useState();
  const [activeTab, setActiveTab] = useState('faturamento');
  const [isSaving, setIsSaving] = useState(false);
  const [gravado, setGravado] = useState(false);
  const codConveniadoRef = useRef(null);
  const handleGetNomeMedico = useGetNomeMedico(medicos, setFatura);

  useEffect(() => {
    if (!show) {
      setSuccessMsg('');
      setActiveTab('faturamento');
      setShowAlertEspelho(false);
      setFaturado(undefined);
      setIsSaving(false);
      setGravado(false);
      return;
    }
    if (!loading && codConveniadoRef.current) {
      codConveniadoRef.current.focus();
    }
  }, [show, loading]);

  const handleFocusGravarCreate = useCallback(e => {
    if (e.key === 'Enter') {
      document.getElementById('btnInserir')?.focus();
    }
  }, []);

  const handleGravarCreate = useCallback(
    async e => {
      if (isSaving || gravado) return;
      setIsSaving(true);
      try {
        const insertResult = await handleInsertFaturamento(e);
        if (!insertResult) return;

        setGravado(true);
        setSuccessMsg(insertResult.message || 'Faturamento salvo com sucesso!');
        setShowAlertEspelho(insertResult.showAlertEspelho);
        if (insertResult.faturado) {
          setFaturado(insertResult.faturado);
        }
      } finally {
        setIsSaving(false);
      }
    },
    [gravado, handleInsertFaturamento, isSaving]
  );

  const isEcoFetal = Number(fatura.codProcedi) === 17;
  const isRetornoContext =
    Number(fatura.codProcedi) === 58 ||
    Number(fatura.codProcediConsulta) === 58;
  const labelConsultaOuRetorno = isRetornoContext ? 'Retorno' : 'Consulta';

  return (
    <Modal show={show} onHide={onHide} size="xl">
      <Modal.Header>FATURAMENTO</Modal.Header>
      {loading ? (
        <div
          className="text-center py-5"
          role="status"
          aria-label="Carregando faturamento"
        >
          <Spinner animation="border" variant="primary" />
        </div>
      ) : (
        <Tabs
          id="faturamento-tabs"
          activeKey={activeTab}
          onSelect={key => setActiveTab(key)}
          className="mb-2"
        >
          <Tab eventKey="faturamento" title="Faturamento">
            <Form.Row className="m-1">
              {[1, 3, 58].includes(Number(fatura.codProcedi)) && (
                <>
                  <Form.Label as={Col} sm={3} className="m-1 font-weight-bold">
                    {`ECG e ${labelConsultaOuRetorno}?`}
                  </Form.Label>
                  <Form.Group className="m-0 mb-2" as={Col} sm={3}>
                    <Form.Check
                      className="m-0"
                      label="Somente ECG"
                      name="groupEcg"
                      id="3"
                      checked={fatura.ecg === '3'}
                      onChange={e => optarPorEcg(e.target.id)}
                    />
                  </Form.Group>
                  <Form.Group className="m-0" as={Col} sm={3}>
                    <Form.Check
                      className="m-0"
                      label={`Somente ${labelConsultaOuRetorno}`}
                      name="groupEcg"
                      id="1"
                      checked={fatura.ecg === '1'}
                      onChange={e => optarPorEcg(e.target.id)}
                    />
                  </Form.Group>
                  <Form.Group className="m-0" as={Col} sm={2}>
                    <Form.Check
                      className="m-0"
                      label={`ECG e ${labelConsultaOuRetorno}`}
                      name="groupEcg"
                      id="31"
                      checked={fatura.ecg === '31'}
                      onChange={e => optarPorEcg(e.target.id)}
                    />
                  </Form.Group>
                </>
              )}
            </Form.Row>
            <Form.Row className="m-1">
              <Form.Group className="m-0" size="sm" as={Col} sm={2}>
                Data:
              </Form.Group>
              <Form.Group className="m-0" as={Col} sm={6}>
                <Form.Control
                  readOnly={true}
                  className="m-0"
                  name="dataFatura"
                  value={fatura?.dataFormatada || ''}
                />
              </Form.Group>
              <Form.Group className="m-0" size="sm" as={Col} sm={1}>
                Func:
              </Form.Group>
              <Form.Group className="m-0" as={Col} sm={3}>
                <Form.Control
                  readOnly={true}
                  name="usuario"
                  value={fatura.usuario || ''}
                />
              </Form.Group>
              <Form.Group className="mt-1" size="sm" as={Col} sm={2}>
                Paciente:
              </Form.Group>
              <Form.Group className="mt-1" size="sm" as={Col} sm={2}>
                <Form.Control
                  readOnly={true}
                  name="codPac"
                  value={fatura.codPac || ''}
                />
              </Form.Group>
              <Form.Group className="mt-1" size="sm" as={Col} sm={8}>
                <Form.Control
                  readOnly={true}
                  name="paciente"
                  value={fatura.paciente || ''}
                />
              </Form.Group>
              <Form.Group className="m-0" size="sm" as={Col} sm={2}>
                Conveniado:
              </Form.Group>
              <Form.Group className="m-0" as={Col} sm={2}>
                <Form.Control
                  size="sm"
                  type="number"
                  id="codConveniado"
                  ref={codConveniadoRef}
                  onKeyUp={e =>
                    handleGetNomeMedico(e, 'codSolicitou', 'conveniado')
                  }
                  onChange={e => handleChangeFaturamento(e)}
                  name="codConveniado"
                  value={fatura.codConveniado || ''}
                />
              </Form.Group>
              <Form.Group className="m-0" as={Col} sm={8}>
                <SelectComponent
                  name="conveniado"
                  codValue={fatura.codConveniado}
                  setFatura={setFatura}
                  options={selectMedicos}
                  codField="codConveniado"
                  textField="conveniado"
                  placeholder="Selecione o Conveniado"
                />
              </Form.Group>
              <Form.Group className="m-0" size="sm" as={Col} sm={2}>
                Solicitou:
              </Form.Group>
              <Form.Group className="m-0" as={Col} sm={2}>
                <Form.Control
                  size="sm"
                  type="number"
                  id="codSolicitou"
                  onKeyUp={e =>
                    handleGetNomeMedico(e, 'codRealizou', 'solicitou')
                  }
                  onChange={e => handleChangeFaturamento(e)}
                  name="codSolicitou"
                  value={fatura.codSolicitou || ''}
                />
              </Form.Group>
              <Form.Group className="m-0" as={Col} sm={8}>
                <SelectComponent
                  name="solicitou"
                  codValue={fatura.codSolicitou}
                  setFatura={setFatura}
                  options={selectMedicos}
                  codField="codSolicitou"
                  textField="solicitou"
                  placeholder="Selecione quem Solicitou"
                />
              </Form.Group>
              <Form.Group className="m-0" size="sm" as={Col} sm={2}>
                Realizou:
              </Form.Group>
              <Form.Group className="m-0" as={Col} sm={2}>
                <Form.Control
                  size="sm"
                  type="number"
                  id="codRealizou"
                  onKeyUp={e =>
                    handleGetNomeMedico(e, 'codFaturou', 'realizou')
                  }
                  onChange={e => handleChangeFaturamento(e)}
                  name="codRealizou"
                  value={fatura.codRealizou || ''}
                />
              </Form.Group>
              <Form.Group className="m-0" as={Col} sm={8}>
                <SelectComponent
                  name="realizou"
                  codValue={fatura.codRealizou}
                  setFatura={setFatura}
                  options={selectMedicos}
                  codField="codRealizou"
                  textField="realizou"
                  placeholder="Selecione quem Realizou"
                />
              </Form.Group>
              <Form.Group className="m-0" size="sm" as={Col} sm={2}>
                Faturou:
              </Form.Group>
              <Form.Group className="m-0" as={Col} sm={2}>
                <Form.Control
                  size="sm"
                  type="number"
                  id="codFaturou"
                  onKeyUp={e => handleGetNomeMedico(e, 'codConv', 'faturou')}
                  onChange={e => handleChangeFaturamento(e)}
                  name="codFaturou"
                  value={fatura.codFaturou || ''}
                />
              </Form.Group>
              <Form.Group className="m-0" as={Col} sm={8}>
                <SelectComponent
                  name="faturou"
                  codValue={fatura.codFaturou}
                  setFatura={setFatura}
                  options={selectMedicos}
                  codField="codFaturou"
                  textField="faturou"
                  placeholder="Selecione quem Faturou"
                />
              </Form.Group>
              <Form.Label as={Col} className="m-0" size="sm">
                Convênio:
              </Form.Label>
              <Form.Group as={Col} className="m-0" sm={2}>
                <Form.Control
                  size="sm"
                  type="number"
                  id="codConv"
                  onKeyUp={e => {
                    if (e.key === 'Enter') {
                      const filteredConv = convenios.filter(
                        i => i.codConv === Number(e.target.value)
                      );
                      if (filteredConv.length > 0) {
                        setFatura({
                          ...fatura,
                          codConv: filteredConv[0].codConv,
                          convenio: filteredConv[0].sigla,
                        });
                        goToNextInput(e, 'codProcedi');
                      } else {
                        setFatura({
                          ...fatura,
                          codConv: '',
                          convenio: '',
                        });
                        goToNextInput(e, 'codConv');
                      }
                    }
                  }}
                  onChange={e => {
                    if (!e.target.value) {
                      setFatura(prev => ({
                        ...prev,
                        codConv: '',
                        convenio: '',
                      }));
                    } else {
                      handleChangeFaturamento(e);
                    }
                  }}
                  name="codConv"
                  value={fatura.codConv || ''}
                />
              </Form.Group>
              <Form.Group className="m-0" as={Col} sm={8}>
                <SelectComponent
                  name="convenio"
                  codValue={fatura.codConv}
                  setFatura={setFatura}
                  options={opcoesConvenio}
                  codField="codConv"
                  textField="convenio"
                  placeholder="Selecione um Convênio"
                />
              </Form.Group>
              <Form.Group className="m-0" size="sm" as={Col} sm={2}>
                Proced:
              </Form.Group>
              <Form.Group className="m-0" as={Col} sm={2}>
                <Form.Control
                  size="sm"
                  id="codProcedi"
                  type="number"
                  style={{ backgroundColor: 'yellow' }}
                  onKeyUp={e => {
                    if (
                      e.key === 'Enter' &&
                      (fatura.ecg !== '31' || Number(e.target.value) !== 3)
                    ) {
                      handleValorFaturamento(e);
                      goToNextInput(e, 'guia');
                    }
                  }}
                  onBlur={e => {
                    if (fatura.ecg !== '31' || Number(e.target.value) !== 3) {
                      handleValorFaturamento(e);
                    }
                  }}
                  onChange={e => handleChangeFaturamento(e)}
                  name="codProcedi"
                  value={fatura.codProcedi || ''}
                />
              </Form.Group>
              <Form.Group className="m-0" as={Col} sm={8}>
                <SelectComponent
                  name="procedimento"
                  codValue={fatura.codProcedi}
                  setFatura={setFatura}
                  options={opcoesProcedimento}
                  codField="codProcedi"
                  textField="procedimento"
                  placeholder="Selecione um Procedimento"
                  onSelectValue={val =>
                    handleValorFaturamento({ target: { value: val } })
                  }
                />
              </Form.Group>

              {/* Seção Dinâmica de Quantidade de Fetos (Para ECO FETAL - 17) */}
              {isEcoFetal && (
                <Form.Group
                  as={Col}
                  sm={12}
                  className="mt-2 p-2 bg-light border rounded"
                >
                  <Form.Row className="align-items-center">
                    <Form.Group as={Col} sm={3} className="m-0">
                      <Form.Label className="font-weight-bold m-0" size="sm">
                        Qtd. de Fetos (Gestação):
                      </Form.Label>
                    </Form.Group>
                    <Form.Group as={Col} sm={2} className="m-0">
                      <Form.Control
                        size="sm"
                        type="number"
                        min={1}
                        max={10}
                        name="qtdFetos"
                        value={fatura.qtdFetos || 1}
                        onChange={handleChangeFaturamento}
                      />
                    </Form.Group>
                    <Form.Group as={Col} sm={7} className="m-0">
                      <ButtonGroup size="sm">
                        <Button
                          variant={
                            Number(fatura.qtdFetos) === 1
                              ? 'primary'
                              : 'outline-primary'
                          }
                          onClick={() => setQuantidadeFetos(1)}
                        >
                          Unifetal (1)
                        </Button>
                        <Button
                          variant={
                            Number(fatura.qtdFetos) === 2
                              ? 'primary'
                              : 'outline-primary'
                          }
                          onClick={() => setQuantidadeFetos(2)}
                        >
                          Gemelar (2)
                        </Button>
                        <Button
                          variant={
                            Number(fatura.qtdFetos) === 3
                              ? 'primary'
                              : 'outline-primary'
                          }
                          onClick={() => setQuantidadeFetos(3)}
                        >
                          Trigêmeos (3)
                        </Button>
                        <Button
                          variant={
                            Number(fatura.qtdFetos) === 4
                              ? 'primary'
                              : 'outline-primary'
                          }
                          onClick={() => setQuantidadeFetos(4)}
                        >
                          Quadrigêmeos (4)
                        </Button>
                      </ButtonGroup>
                    </Form.Group>
                  </Form.Row>
                </Form.Group>
              )}

              {/* Seção Específica: ECO (CODPROCEDI = 11) */}
              {Number(fatura.codProcedi) === 11 && (
                <>
                  <Form.Group className="m-0" as={Col} sm={2}>
                    <Form.Label className="mt-1" size="sm">
                      Tipo de Eco:
                    </Form.Label>
                  </Form.Group>
                  <Form.Group className="m-0" as={Col} sm={2}>
                    <Form.Check
                      className="m-2"
                      label="Doppler"
                      name="groupEco"
                      type="radio"
                      id="EcoD"
                      checked={fatura.eco === 'EcoD' || !fatura.eco}
                      onChange={e => setFatura({ ...fatura, eco: e.target.id })}
                    />
                  </Form.Group>
                  <Form.Group className="m-0" as={Col} sm={2}>
                    <Form.Check
                      className="m-2"
                      label="Congênito"
                      name="groupEco"
                      type="radio"
                      id="EcoC"
                      checked={fatura.eco === 'EcoC'}
                      onChange={e => setFatura({ ...fatura, eco: e.target.id })}
                    />
                  </Form.Group>
                  <Form.Group className="m-0" as={Col} sm={6} />
                </>
              )}

              {/* Campos Padrão de Guia / Valor */}
              {fatura.ecg === '31' ? (
                <>
                  <Form.Group className="mt-1" size="sm" as={Col} sm={2}>
                    Guia Cons:
                  </Form.Group>
                  <Form.Group className="mt-1" as={Col} sm={2}>
                    <Form.Control
                      size="sm"
                      type="number"
                      id="guia"
                      onKeyUp={e => goToNextInput(e, 'valor')}
                      onChange={e => handleChangeFaturamento(e)}
                      name="guia"
                      value={fatura.guia || ''}
                    />
                  </Form.Group>
                  <Form.Group className="mt-1" size="sm" as={Col} sm={2}>
                    Guia Ecg:
                  </Form.Group>
                  <Form.Group className="mt-1" as={Col} sm={2}>
                    <Form.Control
                      size="sm"
                      type="number"
                      id="guiaecg"
                      onKeyUp={e => goToNextInput(e, 'valor')}
                      onChange={e => handleChangeFaturamento(e)}
                      name="guiaecg"
                      value={fatura.guiaecg || ''}
                    />
                  </Form.Group>
                  <Form.Group className="mt-1" size="sm" as={Col} sm={4} />
                  <Form.Group className="mt-1" size="sm" as={Col} sm={2}>
                    Valor Cons:
                  </Form.Group>
                  <Form.Group className="mt-1" as={Col} sm={2}>
                    <Form.Control
                      size="sm"
                      type="number"
                      id="valor"
                      onKeyUp={handleFocusGravarCreate}
                      onChange={e => handleChangeFaturamento(e)}
                      name="valor"
                      value={fatura.valor}
                    />
                  </Form.Group>
                  <Form.Group className="mt-1" size="sm" as={Col} sm={2}>
                    Valor Ecg:
                  </Form.Group>
                  <Form.Group className="mt-1" as={Col} sm={2}>
                    <Form.Control
                      size="sm"
                      type="number"
                      id="valorecg"
                      onKeyUp={handleFocusGravarCreate}
                      onChange={e => handleChangeFaturamento(e)}
                      name="valorecg"
                      value={fatura.valorecg}
                    />
                  </Form.Group>
                </>
              ) : fatura.ecg === '3' ? (
                <>
                  <Form.Group className="mt-1" size="sm" as={Col} sm={2}>
                    Guia Ecg:
                  </Form.Group>
                  <Form.Group className="mt-1" as={Col} sm={2}>
                    <Form.Control
                      size="sm"
                      type="number"
                      id="guiaecg"
                      onKeyUp={e => goToNextInput(e, 'valor')}
                      onChange={e => handleChangeFaturamento(e)}
                      name="guiaecg"
                      value={fatura.guiaecg || ''}
                    />
                  </Form.Group>
                  <Form.Group className="mt-1" size="sm" as={Col} sm={2}>
                    Valor Ecg:
                  </Form.Group>
                  <Form.Group className="mt-1" as={Col} sm={2}>
                    <Form.Control
                      size="sm"
                      type="number"
                      id="valorecg"
                      onKeyUp={handleFocusGravarCreate}
                      onChange={e => handleChangeFaturamento(e)}
                      name="valorecg"
                      value={fatura.valorecg}
                    />
                  </Form.Group>
                </>
              ) : (
                <>
                  <Form.Group className="mt-1" size="sm" as={Col} sm={2}>
                    Guia:
                  </Form.Group>
                  <Form.Group className="mt-1" as={Col} sm={2}>
                    <Form.Control
                      size="sm"
                      type="number"
                      id="guia"
                      onKeyUp={e => goToNextInput(e, 'valor')}
                      onChange={e => handleChangeFaturamento(e)}
                      name="guia"
                      value={fatura.guia || ''}
                    />
                  </Form.Group>
                  <Form.Group className="mt-1" size="sm" as={Col} sm={1}>
                    Valor:
                  </Form.Group>
                  <Form.Group className="mt-1" as={Col} sm={2}>
                    <Form.Control
                      size="sm"
                      type="number"
                      id="valor"
                      onKeyUp={handleFocusGravarCreate}
                      onChange={e => handleChangeFaturamento(e)}
                      name="valor"
                      value={fatura.valor || 0}
                    />
                  </Form.Group>
                </>
              )}
            </Form.Row>
            <Card.Footer className="text-center">
              <Button
                id="btnInserir"
                disabled={isSaving || gravado}
                onClick={handleGravarCreate}
                aria-label="Gravar faturamento"
              >
                {isSaving ? 'Gravando...' : 'Gravar'}
              </Button>{' '}
              <Button
                variant="secondary"
                disabled={isSaving}
                onClick={onHide}
                aria-label="Fechar faturamento"
              >
                Fechar
              </Button>
            </Card.Footer>
            <Alert show={showAlertEspelho} variant="success">
              <Alert.Heading>
                {successMsg && <div>{successMsg}</div>}
              </Alert.Heading>
              <Alert.Heading>Confirma a Impressão do Espelho?</Alert.Heading>
              <div>
                {faturado?.map((a, idx) =>
                  a.CODPROCEDI === 12 ? (
                    <Form.Check
                      key={`espelho-${a.CODLAUDO}-12`}
                      id={`espelho-${a.CODLAUDO}-ete`}
                      inline
                      type="radio"
                      name="radioEspelho"
                      label="Espelho ETE"
                      size="sm"
                      onChange={() => handleEspelhoTrans(a)}
                    />
                  ) : a.CODPROCEDI === 13 ? (
                    <React.Fragment key={`espelho-${a.CODLAUDO}-13`}>
                      <Form.Check
                        id={`espelho-${a.CODLAUDO}-farmaco`}
                        inline
                        type="radio"
                        name="radioEspelho"
                        label="Espelho Farmaco"
                        size="sm"
                        onChange={() => handleEspelhoFarmaco(a)}
                      />
                      <Form.Check
                        id={`espelho-${a.CODLAUDO}-fisio`}
                        inline
                        type="radio"
                        name="radioEspelho"
                        label="Espelho Fisio"
                        size="sm"
                        onChange={() => handleEspelhoFisio(a)}
                      />
                    </React.Fragment>
                  ) : a.CODPROCEDI === 11 ||
                    a.CODPROCEDI === 712 ||
                    a.CODPROCEDI === 713 ||
                    a.CODPROCEDI === 714 ? (
                    <Form.Check
                      key={`espelho-${a.CODLAUDO}-eco`}
                      id={`espelho-${a.CODLAUDO}-eco`}
                      inline
                      type="radio"
                      name="radioEspelho"
                      label={a.DESCPROCEDI}
                      size="sm"
                      onChange={() => handleEspelhoDoppler(a)}
                    />
                  ) : memoAtividade.includes(a.CODPROCEDI) ? (
                    <Form.Check
                      key={`espelho-${a.CODLAUDO}-${a.CODPROCEDI}-${idx}`}
                      id={`espelho-${a.CODLAUDO}`}
                      inline
                      type="radio"
                      name="radioEspelho"
                      label={
                        a.OBSERVACAO ||
                        a.DESCPROCEDI ||
                        `Espelho Feto ${idx + 1}`
                      }
                      size="sm"
                      onChange={() => handleImprimirEspelho(a)}
                    />
                  ) : null
                )}
              </div>
            </Alert>
          </Tab>
          <Tab eventKey="cadastro" title="Cadastro de Médico">
            <NovoMedicoForm />
          </Tab>
        </Tabs>
      )}
    </Modal>
  );
};

export default memo(FaturamentoModalCreate);
